// Where the wizard goes, and whether it exists at all here.

import type { QueryClient } from '@tanstack/react-query';
import { getRuntimeMode, sessionStore } from '@/lib/auth';
import { runtimeFlags } from '@/lib/env';
import { readStore } from '@/lib/storage';
import { onboardingStore } from '../state';
import { claimInvites, fetchIdentityName, IDENTITY_KEY, type ClaimedMembership } from './api';

export const ONB = {
  role: '/onboarding',
  join: '/onboarding/join',
  phone: '/onboarding/phone',
  otp: '/onboarding/otp',
  pin: '/onboarding/pin',
  /** «ما اسمك؟» — the owner's own name, between the PIN and the business name. */
  you: '/onboarding/you',
  business: '/onboarding/business',
  name: '/onboarding/business/name',
  type: '/onboarding/business/type',
  city: '/onboarding/business/city',
  branches: '/onboarding/business/branches',
  address: '/onboarding/business/address',
  bphone: '/onboarding/business/phone',
  hours: '/onboarding/hours',
  workdays: '/onboarding/workdays',
  handover: '/onboarding/handover',
  shape: '/onboarding/shape',
  done: '/onboarding/done',
} as const;

/**
 * Online only: an intranet node has its own first run (/setup) and no
 * Authentik. Under the mock interceptor the node is the dev override
 * `erp.mock.mode=node` (the mock /erp/auth/status always says node).
 */
export function onboardingAvailable(): boolean {
  if (runtimeFlags.mocks) return readStore('erp.mock.mode') !== 'node';
  return getRuntimeMode() !== 'node';
}

/**
 * After the account exists (PIN set): claim any pending invites (phone-based,
 * ADR-0016) and pick the next screen. A person who already belongs to a
 * business goes straight to «اليوم» in it — the profile upsert is one business
 * per owner, so an owner must never walk the create step again. A member
 * lands on /onboarding/join, which shows «ضمن الفريق» or «بانتظار دعوة».
 * A new owner is asked «ما اسمك؟» (/onboarding/you) before the business name,
 * unless their business identity already has a name (profile's
 * business_identities; a read that fails asks — the question is the safe side).
 * `qc`: the read name is handed to the screens' shared identity query, so
 * «ما اسمك؟» does not ask profile a second time.
 */
export async function nextAfterAccount(qc?: QueryClient): Promise<string> {
  let memberships: ClaimedMembership[] = [];
  try {
    memberships = await claimInvites();
  } catch {
    memberships = [];
  }
  onboardingStore.set({ memberships });
  const { intent, createdId } = onboardingStore.get();
  if (intent === 'member') return ONB.join;
  if (createdId) return ONB.done;
  if (memberships.length > 0) {
    const own = memberships.find((m) => m.role === 'owner') ?? memberships[0];
    sessionStore.setBusinessId(own.business_id);
    return '/today';
  }
  let named = '';
  try {
    named = await fetchIdentityName();
    onboardingStore.set({ ownerName: named });
    qc?.setQueryData(IDENTITY_KEY, named);
  } catch {
    named = '';
  }
  return named ? ONB.name : ONB.you;
}
